import {
  buildCollection,
  buildEntityCallbacks,
  buildProperty,
  type CollectionActionsProps,
  type EntityIdUpdateProps,
  type EntityOnSaveProps,
} from "@firecms/core";
import { canAdminEvent, getEventPath, type EventCollectionId } from "../utils";
import type { AuthExtras } from "../types/auth";
import type { EventMemberRole } from "./eventUsers";
import { getAdminT } from "../i18n/getAdminT";
import { getAdminUiLocale } from "../i18n/getAdminUiLocale";
import type { Locale } from "../localization/constants";
import { BulkInviteEventMembersButton } from "../actions/BulkInviteEventMembersButton";
import { buildSendEventInvitationEmailEntityAction } from "../actions/buildSendEventInvitationEmailEntityAction";
import {
  invitationEntityIdFromEmail,
  normalizeInvitationEmail,
} from "./invitations";
import { NAV_GROUP_ACCESS_MANAGEMENT } from "./navigationGroups";
import { EventInvitationEmailSentPreview } from "../properties/eventInvitationEmailSentPreview";

export type EventInvitationStatus = "pending" | "accepted";

export type EventInvitation = {
  email: string;
  role: EventMemberRole;
  status: EventInvitationStatus;
  invitedBy: string;
  createdAt?: Date;
  /** Last successful invitation email. Missing means it has not been sent. */
  emailSentAt?: Date;
  acceptedAt?: Date;
  acceptedByUserId?: string;
};

export const buildEventInvitationsPath = (id: EventCollectionId) =>
  `${getEventPath(id)}/eventInvitations`;

export const buildEventInvitationsCollection = (
  id: EventCollectionId,
  hideFromNavigation?: boolean,
  locale: Locale = getAdminUiLocale()
) => {
  const t = getAdminT(locale);

  const roleEnumValues = {
    participant: t("eventInvitations.roles.participant"),
    operator: t("eventInvitations.roles.operator"),
    presenter: t("eventInvitations.roles.presenter"),
    manager: t("eventInvitations.roles.manager"),
  };

  const statusEnumValues = {
    pending: t("eventInvitations.status.pending"),
    accepted: t("eventInvitations.status.accepted"),
  };

  return buildCollection<EventInvitation>({
    name: t("eventInvitations.collectionName"),
    singularName: t("eventInvitations.singularName"),
    description: t("eventInvitations.description"),
    path: buildEventInvitationsPath(id),
    id: "event_invitations",
    icon: "Mail",
    group: NAV_GROUP_ACCESS_MANAGEMENT,
    hideFromNavigation,
    defaultSize: "xs",
    defaultViewMode: "table",
    hideIdFromForm: true,
    previewProperties: ["email", "role", "status", "emailSentAt"],
    propertiesOrder: ["email", "role", "status", "emailSentAt"],
    permissions: ({ authController }) => {
      const extra = authController.extra as AuthExtras | undefined;
      const canManage = canAdminEvent(id, extra);
      return {
        read: canManage,
        edit: false,
        create: canManage,
        delete: canManage,
      };
    },
    entityActions: [
      buildSendEventInvitationEmailEntityAction(id.accountId, id.eventId),
    ],
    Actions: ({
      selectionController: _selectionController,
    }: CollectionActionsProps<EventInvitation>) => [
      <BulkInviteEventMembersButton key="bulk-invite-event-members" />,
    ],
    callbacks: buildEntityCallbacks({
      onIdUpdate({ values }: EntityIdUpdateProps): string {
        const email =
          typeof values?.email === "string" ? values.email : "";
        return invitationEntityIdFromEmail(email);
      },
      onPreSave({
        values,
        status,
        context,
      }: EntityOnSaveProps<EventInvitation>) {
        const email = normalizeInvitationEmail(values.email ?? "");
        if (!email) {
          throw new Error("Email is required");
        }
        values.email = email;
        if (status === "new") {
          values.status = "pending";
          const uid = context.authController.user?.uid;
          if (!uid) {
            throw new Error("Authentication required");
          }
          values.invitedBy = uid;
          values.createdAt = new Date();
        }
        return values;
      },
    }),
    properties: {
      email: buildProperty({
        name: t("eventInvitations.fields.email"),
        dataType: "string",
        validation: { required: true },
        description: t("eventInvitations.fields.emailDescription"),
      }),
      role: buildProperty({
        name: t("eventInvitations.fields.role"),
        dataType: "string",
        validation: { required: true },
        enumValues: roleEnumValues,
      }),
      status: buildProperty({
        name: t("eventInvitations.fields.status"),
        dataType: "string",
        readOnly: true,
        enumValues: statusEnumValues,
      }),
      emailSentAt: buildProperty({
        name: t("eventInvitations.fields.emailSentAt"),
        dataType: "date",
        mode: "date_time",
        readOnly: true,
        columnWidth: 200,
        Preview: EventInvitationEmailSentPreview,
        customProps: {
          notSentLabel: t("eventInvitations.fields.emailNotSent"),
          locale,
        },
      }),
      invitedBy: buildProperty({
        name: t("eventInvitations.fields.invitedBy"),
        dataType: "string",
        readOnly: true,
        hideFromCollection: true,
      }),
      createdAt: buildProperty({
        name: "Created at",
        dataType: "date",
        readOnly: true,
        hideFromCollection: true,
      }),
      acceptedAt: buildProperty({
        name: t("eventInvitations.fields.acceptedAt"),
        dataType: "date",
        readOnly: true,
        hideFromCollection: true,
      }),
      acceptedByUserId: buildProperty({
        name: t("eventInvitations.fields.acceptedBy"),
        dataType: "string",
        readOnly: true,
        hideFromCollection: true,
      }),
    },
  });
};
